import React from 'react';
import { cn } from '../utils/cn';

/**
 * Select Component - Atom
 * 
 * @param {Object} props
 * @param {string} props.size - 'sm' | 'md' | 'lg'
 * @param {string} props.variant - 'default' | 'error' | 'success'
 * @param {boolean} props.disabled - Disabled state
 */
const Select = React.forwardRef(
  (
    {
      size = 'md',
      variant = 'default',
      className = '',
      disabled = false,
      children,
      ...props
    },
    ref
  ) => {
    const baseStyles = 'w-full rounded-xl border-2 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed appearance-none bg-no-repeat bg-right shadow-sm hover:shadow-md focus:shadow-lg cursor-pointer';

    const variants = {
      default: 'border-primary-200 dark:border-primary-700 bg-white dark:bg-primary-800 text-primary-900 dark:text-primary-50 focus:border-secondary-500 dark:focus:border-secondary-400 focus:ring-secondary-500/50 dark:focus:ring-secondary-400/50',
      error: 'border-error-400 dark:border-error-600 bg-error-50 dark:bg-error-900/30 text-error-900 dark:text-error-50 focus:border-error-500 dark:focus:border-error-500 focus:ring-error-500/50 dark:focus:ring-error-500/50',
      success: 'border-success-400 dark:border-success-600 bg-success-50 dark:bg-success-900/30 text-success-900 dark:text-success-50 focus:border-success-500 dark:focus:border-success-500 focus:ring-success-500/50 dark:focus:ring-success-500/50',
    };

    const sizes = {
      sm: 'px-3 py-2 pr-10 text-sm',
      md: 'px-4 py-2.5 pr-12 text-base',
      lg: 'px-5 py-3 pr-12 text-lg',
    };

    // Custom dropdown arrow SVG - using ChevronDown icon
    const getArrowColor = () => {
      if (variant === 'error') return 'rgb(239, 68, 68)';
      if (variant === 'success') return 'rgb(34, 197, 94)';
      return 'rgb(100, 116, 139)'; // primary-400
    };

    const arrowColor = getArrowColor();
    const arrowSvg = encodeURIComponent(`
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="${arrowColor}" stroke-width="2.5">
        <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
      </svg>
    `);

    const paddingRight = size === 'sm' ? '2.5rem' : size === 'md' ? '3rem' : '3rem';

    return (
      <div className="relative">
        <select
          ref={ref}
          disabled={disabled}
          className={cn(
            baseStyles,
            variants[variant],
            sizes[size],
            className
          )}
          style={{
            backgroundImage: `url("data:image/svg+xml,${arrowSvg}")`,
            backgroundSize: '1.25rem 1.25rem',
            backgroundPosition: `right 0.75rem center`,
            paddingRight: paddingRight,
          }}
          {...props}
        >
          {children}
        </select>
      </div>
    );
  }
);

Select.displayName = 'Select';

export default Select;
